import { useSelector } from "@tanstack/react-store";
import type { RowData } from "@tanstack/react-table";
import classNames from "classnames";
import { numberFormatter } from "@common/helpers/number";
import { Button } from "@design-system/Button/Button";
import { HStack } from "@design-system/HStack/HStack";
import { useDataGridContext } from "../../DataGridContext";
import type { DataGridInstance } from "../../types";
import styles from "./SelectAllMatching.module.scss";

interface DataGridSelectAllMatchingProps {
    className?: string;
}

interface SelectAllMatchingBarProps<
    TData extends RowData,
> extends DataGridSelectAllMatchingProps {
    instance: DataGridInstance<TData>;
    noun: string;
}

const SelectAllMatchingBar = <TData extends RowData>({
    instance,
    noun,
    className,
}: Readonly<SelectAllMatchingBarProps<TData>>) => {
    const { table, atoms, totalRowCount, hasMoreRows, loadMore } = instance;
    const isAllMatching = useSelector(atoms.allMatchingSelected);
    const loadedRowCount = useSelector(table.atoms.rowSelection, () =>
        table.getIsAllRowsSelected() ? table.getCoreRowModel().rows.length : 0,
    );
    const hasUnloadedRows = loadedRowCount < totalRowCount || hasMoreRows;
    if (!isAllMatching && (loadedRowCount === 0 || !hasUnloadedRows)) {
        return null;
    }
    const total =
        totalRowCount > loadedRowCount
            ? numberFormatter.format(totalRowCount)
            : "matching";
    const loaded = numberFormatter.format(loadedRowCount);
    return (
        <HStack
            gap={8}
            align="center"
            className={classNames(styles.selectAllMatching, className)}
            role="status"
        >
            {isAllMatching ? (
                <>
                    <span>
                        All {total} {noun} selected
                    </span>
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                            atoms.allMatchingSelected.set(false);
                            table.setRowSelection({});
                        }}
                    >
                        Deselect all
                    </Button>
                </>
            ) : (
                <>
                    <span>
                        {loadMore
                            ? `All ${loaded} loaded ${noun} selected`
                            : `All ${loaded} ${noun} on this page selected`}
                    </span>
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => atoms.allMatchingSelected.set(true)}
                    >
                        Select all {total} {noun}
                    </Button>
                </>
            )}
        </HStack>
    );
};

/**
 * For server-paged and load-more tables: once every loaded row is selected,
 * offers to select all matching rows (`rowCount` when known). Read the flag from
 * the `useDataGrid` result's `atoms.allMatchingSelected`, or from
 * `rowSelection.onAllMatchingChange`.
 */
export const DataGridSelectAllMatching = (
    props: Readonly<DataGridSelectAllMatchingProps>,
) => {
    const { visit, noun } = useDataGridContext();
    return visit((instance) => (
        <SelectAllMatchingBar {...props} instance={instance} noun={noun} />
    ));
};
